import React, { useCallback, useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { formatDateForRegion, type Region } from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { Button, Field, FieldDescription, FieldLabel } from '@/components/ui';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

function sortYmdDates(dates: string[]): string[] {
  return [...dates].sort();
}

export type PrivatePackageSessionDatesFieldProps = {
  selectedDates: string[];
  onChange: (dates: string[]) => void;
  initialFocusDate?: Date;
  region?: Region | null;
  disabled?: boolean;
  showDescription?: boolean;
};

export function PrivatePackageSessionDatesField({
  selectedDates,
  onChange,
  initialFocusDate,
  region,
  disabled = false,
  showDescription = true,
}: PrivatePackageSessionDatesFieldProps) {
  const [currentMonth, setCurrentMonth] = useState(() =>
    startOfMonth(initialFocusDate ?? new Date())
  );

  const selectedSet = useMemo(() => new Set(selectedDates), [selectedDates]);
  const sortedDates = useMemo(() => sortYmdDates(selectedDates), [selectedDates]);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [calendarStart, calendarEnd]);

  const todayStart = useMemo(() => startOfDay(new Date()), []);

  const toggleDate = useCallback(
    (ymd: string) => {
      if (disabled) return;
      if (selectedSet.has(ymd)) {
        onChange(selectedDates.filter((d) => d !== ymd));
      } else {
        onChange(sortYmdDates([...selectedDates, ymd]));
      }
    },
    [disabled, onChange, selectedDates, selectedSet]
  );

  const removeDate = useCallback(
    (ymd: string) => {
      if (disabled) return;
      onChange(selectedDates.filter((d) => d !== ymd));
    },
    [disabled, onChange, selectedDates]
  );

  const formatChipLabel = (ymd: string) =>
    formatDateForRegion(ymd, region, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const monthLabel = format(currentMonth, 'MMMM yyyy');

  return (
    <Field data-disabled={disabled ? true : undefined} className="group/field">
      <FieldLabel className="text-gray-700 dark:text-gray-300">Session dates</FieldLabel>
      {showDescription ? (
        <FieldDescription>
          Click days on the calendar to add or remove sessions.
        </FieldDescription>
      ) : null}

      <div className="mt-2 space-y-4">
        {/* Mini month calendar */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 sm:p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{monthLabel}</h4>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={disabled}
                aria-label="Previous month"
                onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={disabled}
                aria-label="Next month"
                onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>

          <div
            role="grid"
            aria-label={`Session date calendar, ${monthLabel}`}
            className="space-y-1"
          >
            <div
              role="row"
              className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 sm:gap-1 sm:text-xs"
            >
              {WEEKDAY_LABELS.map((label, index) => (
                <div key={label} role="columnheader" className="py-0.5">
                  <span className="sm:hidden" aria-hidden>
                    {WEEKDAY_LETTERS[index]}
                  </span>
                  <span className="hidden sm:inline">{label}</span>
                </div>
              ))}
            </div>

            <div role="rowgroup" className="grid grid-cols-7 gap-0.5 sm:gap-1">
              {calendarDays.map((day) => {
                const ymd = format(day, 'yyyy-MM-dd');
                const isSelected = selectedSet.has(ymd);
                const isCurrentMonth = isSameMonth(day, currentMonth);
                const isToday = isSameDay(day, todayStart);
                const dayLabel = formatDateForRegion(day, region, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });

                return (
                  <div key={ymd} role="gridcell" className="flex items-center justify-center p-0.5">
                    <button
                      type="button"
                      disabled={disabled}
                      aria-pressed={isSelected}
                      aria-label={`${dayLabel}${isSelected ? ', selected' : ''}`}
                      onClick={() => toggleDate(ymd)}
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-md text-xs font-medium tabular-nums transition-colors sm:h-9 sm:w-9 sm:text-sm',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        isSelected
                          ? 'border border-orange-500 bg-orange-100 text-orange-900 shadow-sm dark:border-orange-500 dark:bg-orange-900/40 dark:text-orange-100'
                          : isToday
                            ? 'border border-violet-400 bg-violet-50 text-violet-900 dark:border-violet-500 dark:bg-violet-900/30 dark:text-violet-100'
                            : isCurrentMonth
                              ? 'border border-transparent text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700'
                              : 'border border-transparent text-gray-400 hover:bg-gray-50 dark:text-gray-500 dark:hover:bg-gray-700/50'
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected dates chips */}
        {sortedDates.length > 0 ? (
          <ul className="flex flex-wrap gap-2" aria-label="Selected session dates">
            {sortedDates.map((ymd) => {
              const chipLabel = formatChipLabel(ymd);
              return (
                <li key={ymd}>
                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                    {chipLabel}
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={`Remove ${chipLabel}`}
                      onClick={() => removeDate(ymd)}
                      className="rounded-full p-0.5 hover:bg-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 dark:hover:bg-orange-800/60 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <X className="h-3 w-3" aria-hidden />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No session dates selected.</p>
        )}
      </div>
    </Field>
  );
}
